import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { BoxInfo } from '@lub0v/santa-utils';
import { Button, Checkbox, Hint, Stack, Typography, showDialog } from '@src/components';
import { closeDialog } from '@src/components/Dialog/Dialog';
import { Menu } from '@src/components/Menu/Menu';
import { BoxIcon } from '@src/components/icons/BoxIcon';
import { TrashIcon } from '@src/components/icons/TrashIcon';
import { CopyIcon } from '@src/components/icons/CopyIcon';
import { useDuplicateGameDialog } from './DuplicateGameDialog';
import { santaClient } from '@src/state/client';

function ConfirmBoxAction({ item, action, onChanged }: { item: BoxInfo; action: 'archive' | 'delete'; onChanged: (key: string, archived?: boolean) => void }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const archived = item.box.isArchived;
  async function submit() {
    setBusy(true); setError(false);
    try {
      const result = action === 'delete'
        ? await santaClient.deleteBox({ key: item.box.key })
        : await santaClient.updateBox({ key: item.box.key, name: item.box.name, isArchived: !archived });
      if (!result.ok) throw new Error('Box action failed');
      onChanged(item.box.key, action === 'delete' ? undefined : !archived);
      closeDialog();
    } catch { setError(true); }
    finally { setBusy(false); }
  }
  return <Stack gap={4}>
    <Typography as="p" role="body/strong">{item.box.name}</Typography>
    <p>{t(action === 'delete' ? 'game.settings.deleteBody' : archived ? 'box.organizer.restore.body' : 'box.organizer.archive.body')}</p>
    {error && <Hint tone="error">{t(action === 'delete' ? 'newBox.saveFailed' : archived ? 'box.organizer.restoreFailed' : 'box.organizer.archiveFailed')}</Hint>}
    <BoxActionButtons busy={busy} action={action} archived={Boolean(archived)} onSubmit={() => void submit()} />
  </Stack>;
}

function BoxActionButtons({ busy, action, archived, onSubmit }: { busy: boolean; action: 'archive' | 'delete'; archived: boolean; onSubmit: () => void }) {
  const { t } = useTranslation();
  const [confirmed, setConfirmed] = useState(false);
  return <Stack gap={3}>
    {action === 'delete' && <Checkbox checked={confirmed} disabled={busy} onCheckedChange={setConfirmed} label={<Trans i18nKey="game.settings.deleteConfirmation" components={{ strong: <Typography as="strong" role="body/strong" /> }} />} />}
    <div className="boxes-page__confirm-actions">
      <Button variant="secondary" disabled={busy} onClick={() => closeDialog()}>{t('box.cancel')}</Button>
      <Button variant={action === 'delete' ? 'danger' : 'primary'} disabled={busy || (action === 'delete' && !confirmed)} loading={busy} onClick={onSubmit}>{t(action === 'delete' ? 'game.settings.delete' : archived ? 'box.organizer.restore.submit' : 'box.organizer.archive.submit')}</Button>
    </div>
  </Stack>;
}

export function BoxCardMenu({ item, onChanged }: { item: BoxInfo; onChanged: (key: string, archived?: boolean) => void }) {
  const { t, i18n } = useTranslation();
  const showDuplicateGameDialog = useDuplicateGameDialog('game_list');
  if (!item.isAdmin) return null;
  function confirm(action: 'archive' | 'delete') {
    showDialog({
      title: t(action === 'delete' ? 'game.settings.deleteTitle' : item.box.isArchived ? 'box.organizer.restore.title' : 'box.organizer.archive.title'),
      preventDismiss: true, hideClose: true,
      children: <ConfirmBoxAction item={item} action={action} onChanged={onChanged} />,
    });
  }
  return <div className="boxes-page__card-menu"><Menu
    label={`${i18n.language.startsWith('ru') ? 'Действия с игрой' : 'Game actions'}: ${item.box.name}`}
    variant="secondary"
    testId={`boxes-menu-${item.box.key}`}
    items={[
      { id: 'duplicate', label: t('boxes.duplicate.title'), icon: <CopyIcon />, onSelect: () => showDuplicateGameDialog(item) },
      { id: 'archive', label: t(item.box.isArchived ? 'box.organizer.restore.title' : 'box.organizer.archive.title'), icon: <BoxIcon />, onSelect: () => confirm('archive') },
      { id: 'delete', label: t('game.settings.delete'), icon: <TrashIcon />, danger: true, onSelect: () => confirm('delete') },
    ]}
  /></div>;
}
